import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, UserPlus } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import SummaryGrid from "../../components/UserManagement/SummaryGrid";
import Button from "../../components/Button/Button";
import TeamMembersSection from "../../components/UserManagement/TeamMembersSection";
import InviteUserModal from "../../components/UserManagement/InviteUserModal";
import { UserSkeleton } from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";
import { can } from "../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./Users.module.scss";

const Users = () => {
  const navigate = useNavigate();

  /* ===================== STATE ===================== */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingInvites: 0,
  });

  /* ===================== API CALLS ===================== */
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet("user", "/customer/users?page=1&limit=1");
      if (response.status === "success") {
        setSummaryData({
          totalUsers: response.data.summary.totalUsers,
          activeUsers: response.data.summary.activeUsers,
          pendingInvites: response.data.summary.pendingInvites,
        });
      }
    } catch (error) {
      if (error?.message?.includes("403")) {
        navigate("/page-not-found", { replace: true });
        return;
      }
      console.error("Failed to fetch summary", error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  /* ===================== JSX ===================== */
  return (
    <div className={styles["admin-users__page"]}>
      {/* HEADER SECTION */}
      <div className={styles["admin-users__header"]}>
        <div className={styles["admin-users__icon-wrapper"]}>
          <User size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-users__title-group"]}>
          <h2 className={styles["admin-users__title"]}>Users</h2>
          <p className={styles["admin-users__subtitle"]}>
            Manage team members and their access levels
          </p>
        </div>

        <div className={styles["admin-users__actions"]}>
          {can("users", "invite-user") && (
            <Button
              text="Invite User"
              icon={UserPlus}
              onClick={() => setIsModalOpen(true)}
            />
          )}
        </div>
      </div>

      {/* SUMMARY STATS / SKELETONS */}
      {loading ? (
        <div className={styles["admin-users__skeleton-grid"]}>
          {[...Array(3)].map((_, i) => (
            <UserSkeleton key={i} />
          ))}
        </div>
      ) : (
        <SummaryGrid data={summaryData} />
      )}

      {/* TABLE SECTION */}
      <TeamMembersSection
        refreshTrigger={refreshTrigger}
        userType="Client"
        onSummaryRefresh={fetchSummary}
      />

      {/* MODAL */}
      <InviteUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInviteSuccess={() => setRefreshTrigger((prev) => prev + 1)}
      />
    </div>
  );
};

export default Users;
